import { useCallback, useState } from 'react'
import { Grid } from 'semantic-ui-react'
import { DateTime } from 'luxon'

import { useReselector } from '@/utils/sharedHooks'
import { getFetchError } from '@/reducers/fetch'
import {
  type DisabledProfileSummary,
  ENABLE_PAYROLL_KEY,
  enablePayroll,
} from '@/features/payroll/payrollActions'
import {
  Alert,
  Button,
  GridRowColumn,
  Modal,
  Text,
  Toggle,
} from '@/componentsV1/BaseComponents'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'

const EnrollPayrollModal = ({
  open,
  close,
  userId,
}: {
  open: boolean
  close: () => void
  userId: number
}) => {
  const dispatch = useAppDispatch()
  const [needsMigration, setNeedsMigration] = useState(false)
  const [disabledProfiles, setDisabledProfiles] = useState<
    DisabledProfileSummary[] | null
  >(null)
  const error = useReselector(getFetchError, ENABLE_PAYROLL_KEY)

  const handleClose = useCallback(() => {
    setDisabledProfiles(null)
    close()
  }, [close])

  const enrollUserInPayroll = useCallback(async () => {
    const result = await enablePayroll(userId, needsMigration)(dispatch)
    if (result?.status === 'needs_choice') {
      setDisabledProfiles(result.disabledProfiles)
      return
    }
    if (result?.status === 'enabled') {
      handleClose()
    }
  }, [dispatch, userId, needsMigration, handleClose])

  const reactivateProfile = useCallback(
    async (reactivateProfileId: number) => {
      const result = await enablePayroll(userId, needsMigration, {
        reactivateProfileId,
      })(dispatch)
      if (result?.status === 'enabled') {
        handleClose()
      }
    },
    [dispatch, userId, needsMigration, handleClose]
  )

  const createNewProfile = useCallback(async () => {
    const result = await enablePayroll(userId, needsMigration, {
      forceNew: true,
    })(dispatch)
    if (result?.status === 'enabled') {
      handleClose()
    }
  }, [dispatch, userId, needsMigration, handleClose])

  return (
    <Modal
      size="small"
      className="enrollPayrollModal"
      open={open}
      onClose={handleClose}
      closeIcon
    >
      <Modal.Header>Enroll User in Payroll</Modal.Header>
      <Modal.Content>
        <Grid>
          {error && (
            <GridRowColumn>
              <Alert type="error">Error: {error.message}</Alert>
            </GridRowColumn>
          )}
          {disabledProfiles ? (
            <>
              <GridRowColumn>
                <Text>
                  This user has previously disabled payroll profile(s).
                  Reactivate an existing one to preserve its Gusto connection,
                  or create a new profile.
                </Text>
              </GridRowColumn>
              {disabledProfiles.map((profile) => (
                <GridRowColumn key={profile.id}>
                  <Text>
                    Profile #{profile.id} — Gusto company:{' '}
                    {profile.gustoCompanyUuid ?? 'none'}, created{' '}
                    {DateTime.fromISO(profile.createdAt).toFormat(
                      DATE_FORMATS_LUXON.DISPLAY_SHORT
                    )}
                  </Text>
                  <Button onClick={() => reactivateProfile(profile.id)}>
                    Reactivate Profile #{profile.id}
                  </Button>
                </GridRowColumn>
              ))}
            </>
          ) : (
            <GridRowColumn>
              <Toggle
                label="Migrate User - Check this if the User already has an existing Gusto profile that
                needs to be migrated to Heard"
                checked={needsMigration}
                onChange={() => setNeedsMigration((prev) => !prev)}
              />
            </GridRowColumn>
          )}
        </Grid>
      </Modal.Content>
      <Modal.Actions>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        {disabledProfiles ? (
          <Button onClick={createNewProfile}>Create New Profile</Button>
        ) : (
          <Button onClick={enrollUserInPayroll}>
            Enroll In Payroll for User
          </Button>
        )}
      </Modal.Actions>
    </Modal>
  )
}

export default EnrollPayrollModal
